/* eslint-disable */
// Disabling eslint for the entire file because this is
// an implementation provided by Bryntum.

import * as React from 'react'
import ReactDOM from 'react-dom'
import { Scheduler, SchedulerConfig } from 'bryntum-scheduler'
import 'bryntum-scheduler/scheduler.material.css'
import { ISchedulerViewPreset } from '../container/SchedulerContainer'

export enum SchedulerModes {
  horizontal = 'horizontal',
  vertical = 'vertical'
}

export enum SchedulerViewPresets {
  DEFAULT_PRESET = 'defaultPreset',
  SHOP_VIEW_PRESET = 'shopViewPreset',
  HOUR_ADN_DAY_PRESET = 'hourAndDay'
}

export type SchedulerViewPresetType =
  | SchedulerViewPresets.DEFAULT_PRESET
  | SchedulerViewPresets.SHOP_VIEW_PRESET
  | SchedulerViewPresets.HOUR_ADN_DAY_PRESET

export type ISchedulerConfigWorkingTimeHour = number

export interface ISchedulerConfigWorkingTime {
  fromDay: number
  toDay: number
  fromHour: ISchedulerConfigWorkingTimeHour
  toHour: ISchedulerConfigWorkingTimeHour
}

export interface ISchedulerEngine extends Scheduler {}

export interface ISchedulerConfig extends SchedulerConfig {
  presets: ISchedulerViewPreset[]
  viewPreset: SchedulerViewPresets
  workingTime: ISchedulerConfigWorkingTime
  mode: SchedulerModes
}

type BryntumSchedulerType = Partial<SchedulerConfig> & {
  ref: any
  autoHeight: boolean
  eventsVersion: any
  resourcesVersion: any
  contextMenuFeature: any
  headerZoomFeature: any
  stripeFeature: any
  groupFeature: any
  timeRangesFeature: any
  resourceTimeRangesFeature: any
  sortFeature: any
  eventTooltipFeature: any
  eventContextMenuFeature: any
  eventEditFeature: any
  scheduleContextMenuFeature: any
  eventDragCreateFeature: any
  eventDragFeature: any
  eventResizeFeature: any
  mode: SchedulerModes
}

export default class BryntumScheduler extends React.Component<BryntumSchedulerType> {
  // Features requires special handling when used as props
  schedulerFeatures = [
    'cellEditFeature',
    'columnLinesFeature',
    'contextMenuFeature',
    'dependenciesFeature',
    'eventDragFeature',
    'eventContextMenuFeature',
    'eventDragFeature',
    'eventDragCreateFeature',
    'eventEditFeature',
    'eventFilterFeature',
    'eventResizeFeature',
    'eventTooltipFeature',
    'groupFeature',
    'groupSummaryFeature',
    'headerContextMenuFeature',
    'labelsFeature',
    'nonWorkingTimeFeature',
    'regionResizeFeature',
    'resourceTimeRangesFeature',
    'scheduleContextMenuFeature',
    'sortFeature',
    'scheduleTooltipFeature',
    'stripeFeature',
    'summaryFeature',
    'timeRangesFeature'
  ]

  static defaultProps = {
    viewPreset: SchedulerViewPresets.DEFAULT_PRESET,
    barMargin: 2,
    timeRanges: true,
    eventsVersion: 0,
    resourcesVersion: 0
  }

  public el: HTMLElement = {} as HTMLElement
  public schedulerEngine: Scheduler = {} as Scheduler

  state = {
    portals: new Set(),
    generation: 0
  }

  getSchedulerEngine = (): Scheduler => this.schedulerEngine

  releaseReactCell(cellElement: any) {
    const { state } = this,
      cellElementData = cellElement._domData

    // Cell already has a react component in it, remove
    if (cellElementData.reactPortal) {
      state.portals.delete(cellElementData.reactPortal)

      this.setState({
        portals: state.portals,
        generation: state.generation + 1
      })

      cellElementData.reactPortal = null
    }
  }

  // React component rendered to DOM, render scheduler to it
  componentDidMount() {
    const config: { [key: string]: any } = {
      appendTo: this.el,
      features: {
        eventDragCreate: false
      },
      zoomOnTimeAxisDoubleClick: false,
      // Hook called by engine when rendering cells, creates portals for JSX supplied by renderers
      processCellContent: ({
        cellContent,
        cellElement,
        cellElementData,
        record
      }: {
        cellContent: any
        cellElement: any
        cellElementData: any
        record: any
      }) => {
        let shouldSetContent = cellContent != null

        // Release any existing React component
        this.releaseReactCell(cellElement)

        // Detect React component
        if (cellContent && cellContent.$$typeof === Symbol.for('react.element')) {
          // Excluding special rows for now to keep renderers simpler
          if (!record.meta.specialRow) {
            // Clear any non-react content
            const firstChild = cellElement.firstChild
            if (!cellElementData.reactPortal && firstChild) {
              firstChild.data = ''
            }

            // Create a portal, belonging to the existing React tree but render in a cell
            const portal = ReactDOM.createPortal(cellContent, cellElement)
            cellElementData.reactPortal = portal

            const { state } = this
            // Store in state for React to not loose track of the portals
            state.portals.add(portal)
            this.setState({
              portals: state.portals,
              generation: state.generation + 1
            })
          }
          shouldSetContent = false
        }

        return shouldSetContent
      }
    }

    this.schedulerFeatures.forEach(propName => {
      if (propName in this.props) {
        const value = this.props[propName as keyof BryntumSchedulerType]

        if (value !== undefined) {
          const featureConfigName = this._getFeatureConfigNameFromPropName(propName)
          config.features[featureConfigName] = value
        }
      }
    })

    // Handle config (relaying all props except those used for features to scheduler)
    Object.keys(this.props).forEach(propName => {
      if (!this.schedulerFeatures.includes(propName)) {
        config[propName] = this.props[propName as keyof BryntumSchedulerType]
      }
    })

    // Create the actual scheduler, used as engine for the wrapper
    this.schedulerEngine = new Scheduler(config)

    // Map all features from schedulerEngine to scheduler to simplify calls
    Object.keys(this.schedulerEngine.features).forEach(key => {
      if (!(this as any)[key]) {
        ;(this as any)[key] = this.schedulerEngine.features[key]
      }
    })
  }

  componentWillUnmount() {
    this.schedulerEngine?.destroy?.()
  }

  componentDidUpdate(prevProps: any) {
    const engine = this.schedulerEngine
    const props = this.props

    // These props are ignored or has special handling below
    const excludeProps = [
      'events',
      'resources',
      'eventsVersion',
      'resourcesVersion',
      'timeRanges',
      'workingTime',
      'columns',
      'adapter',
      'ref',
      'children',
      ...this.schedulerFeatures
    ]

    if (props.mode === SchedulerModes.horizontal) {
      // Exclude listeners for horizontal mode ONLY.
      // If listeners are not excluded from being updated, then headers and grid are broken.
      excludeProps.push('listeners')
    }

    // Reflect configuration changes. Since most scheduler configs are reactive the scheduler will update automatically
    Object.keys(props).forEach(propName => {
      // Only apply if prop has changed
      const propValue = props[propName as keyof BryntumSchedulerType]
      if (!excludeProps.includes(propName) && propValue !== prevProps[propName]) {
        const configName = this._getFeatureConfigNameFromPropName(propName)
        ;(engine as any)[configName] = propValue
      }
    })

    // xxVersion used to flag that data has changed
    if (props.resources && prevProps.resourcesVersion !== props.resourcesVersion) {
      engine.resources = props.resources
    }

    if (props.events && prevProps.eventsVersion !== props.eventsVersion) {
      engine.eventStore.data = props.events
    }

    // Reflect feature config changes
    this.schedulerFeatures.forEach(featurePropName => {
      const currentProp = props[featurePropName as keyof BryntumSchedulerType]
      const prevProp = prevProps[featurePropName]

      if (featurePropName in props && currentProp !== prevProp && JSON.stringify(currentProp) !== JSON.stringify(prevProp)) {
        const configName = this._getFeatureConfigNameFromPropName(featurePropName)
        engine.features[configName].setConfig(currentProp)
      }
    })

    // update the first column width
    if (this.props.columns && (this as any).props.columns[0]) {
      // @ts-ignore Bryntum specific types
      engine.columns.first.width = (this as any).props.columns[0].width
    }
  }

  render() {

    return (
      <div className='b-react-scheduler-container' id={this.props.id} ref={el => (this.el = el as HTMLElement)}>
        {this.state.portals}
      </div>
    )
  }

  private _getFeatureConfigNameFromPropName = (featurePropName: string): keyof ISchedulerConfig =>
    featurePropName.replace(/Feature$/, '') as keyof ISchedulerConfig
}
